import { PortalApiError, ekoFetch, ekoJson, portalFetch, portalJson } from './client';

/** Reads the server's machine-readable `error` code (for example "current-password-incorrect"). */
const portalErrorFrom = async (response: Response): Promise<PortalApiError> => {
  let code = `HTTP ${response.status}`;
  try {
    const data = (await response.json()) as { error?: string };
    if (data.error) code = data.error;
  } catch {
    // Not JSON; keep the status-based code.
  }
  return new PortalApiError(response.status, code);
};

export interface PortalProfile {
  customerId: number;
  username: string;
  displayName: string;
  phone: string;
  secondaryPhone: string;
  address: string;
  age: number | null;
  email: string;
}

export interface PortalTest {
  testCode: number;
  name: string;
  children: PortalTest[];
}

export interface PortalVisit {
  visitId: string;
  daySerial: number;
  visitDate: string;
  total: number;
  discountValue: number;
  paid: number;
  remaining: number;
  tests: PortalTest[];
  doctorName: string;
  resultSentOnWhatsApp: boolean;
  resultUrl: string | null;
}

interface WireTest {
  testCode: number;
  name: string;
  children?: WireTest[];
}

type WireVisit = Omit<PortalVisit, 'tests' | 'doctorName' | 'resultSentOnWhatsApp' | 'resultUrl'> & {
  tests?: WireTest[];
  doctorName?: string;
  resultSentOnWhatsApp?: boolean;
  resultUrl?: string | null;
};

interface WireLoginResponse {
  token: string;
  role: string;
}

const mapTest = (test: WireTest): PortalTest => ({
  testCode: test.testCode,
  name: test.name,
  children: (test.children ?? []).map(mapTest),
});

export const mapVisit = (visit: WireVisit): PortalVisit => ({
  visitId: visit.visitId,
  daySerial: visit.daySerial,
  visitDate: visit.visitDate,
  total: visit.total,
  discountValue: visit.discountValue,
  paid: visit.paid,
  remaining: visit.remaining,
  tests: (visit.tests ?? []).map(mapTest),
  doctorName: visit.doctorName ?? '',
  resultSentOnWhatsApp: visit.resultSentOnWhatsApp ?? false,
  resultUrl: visit.resultUrl ?? null,
});

export type PortalRole = 'customer' | 'doctor';

interface LoginInput {
  username: string;
  password: string;
  lang: 'ar' | 'en';
}

/** One login for everyone. The Customer API decides the account type from the lab; the answer carries the role. */
export const loginAccount = async (input: LoginInput): Promise<{ token: string; role: PortalRole }> => {
  const response = await portalFetch('/api/auth/login', {
    method: 'POST',
    body: { username: input.username.trim(), password: input.password },
  });
  if (!response.ok) {
    throw new PortalApiError(response.status === 401 ? 401 : response.status, response.status === 401 ? 'invalid-credentials' : `HTTP ${response.status}`);
  }

  const login = (await response.json()) as WireLoginResponse;
  return { token: login.token, role: login.role.toLowerCase() === 'doctor' ? 'doctor' : 'customer' };
};

/** Swaps a still-valid token for a fresh one. The server refuses (401) once the session is older than its maximum. */
export const refreshToken = async (token: string): Promise<string> =>
  (await portalJson<{ token: string }>('/api/auth/refresh', { method: 'POST', token })).token;

/**
 * The support chat still lives on the lab (EKO) API and needs an EKO token. This signs in there with the same credentials;
 * any failure returns null and only the chat is unavailable.
 */
export const loginForChat = async (input: LoginInput): Promise<string | null> => {
  try {
    await ekoFetch('/api/Config/bootstrap?deviceNumber=1');
    const response = await ekoJson<{ IsSuccess: boolean; Token: string | null }>('/Secuirty/AuthenticateWithType', {
      method: 'POST',
      body: {
        username: input.username.trim(),
        password: input.password,
        type: 'Customer',
        lang: input.lang,
        IsDesktop: false,
        addons: false,
        ClientDateTime: new Date().toISOString(),
      },
    });
    return response.IsSuccess && response.Token ? response.Token : null;
  } catch {
    return null;
  }
};

export const fetchProfile = async (token: string): Promise<PortalProfile> => {
  const profile = await portalJson<Partial<PortalProfile> & Pick<PortalProfile, 'customerId' | 'username' | 'displayName'>>('/api/portal/me', { token });
  return {
    customerId: profile.customerId,
    username: profile.username,
    displayName: profile.displayName,
    phone: profile.phone ?? '',
    secondaryPhone: profile.secondaryPhone ?? '',
    address: profile.address ?? '',
    age: profile.age ?? null,
    email: profile.email ?? '',
  };
};

export const fetchVisits = async (token: string): Promise<PortalVisit[]> => {
  const visits = await portalJson<WireVisit[]>('/api/portal/me/visits', { token });
  return visits.map(mapVisit);
};

export interface PortalPointMovement {
  id: number;
  /** "earned" comes from a paid visit; "used" is points the cashier redeemed. */
  kind: 'earned' | 'used';
  points: number;
  date: string | null;
  visitId: string | null;
  daySerial: number | null;
}

export interface PortalPoints {
  earned: number;
  used: number;
  remaining: number;
  movements: PortalPointMovement[];
}

interface WirePoints {
  earnedPoints: number;
  usedPoints: number;
  remainingPoints: number;
  items: { id: number; transactionType: number; points: number; createdAt: string | null; visitId: number | string | null; daySerial: number | null }[];
}

export const fetchPoints = async (token: string): Promise<PortalPoints> => {
  const body = await portalJson<WirePoints>('/api/portal/me/points', { token });

  // Movements with 0 points carry nothing to show.
  const movements = (body.items ?? [])
    .map((item): PortalPointMovement => ({
      id: item.id,
      kind: item.transactionType === 1 ? 'earned' : 'used',
      points: item.points ?? 0,
      date: item.createdAt || null,
      visitId: item.visitId === null || item.visitId === undefined ? null : String(item.visitId),
      daySerial: item.daySerial ?? null,
    }))
    .filter((movement) => movement.points !== 0);

  return { earned: body.earnedPoints ?? 0, used: body.usedPoints ?? 0, remaining: body.remainingPoints ?? 0, movements };
};

export const RESULT_LINK_PREFIX = 'https://r.elhamylabs.com/';

export const resultLinkFor = (visitId: string): string => `${RESULT_LINK_PREFIX}r/${encodeURIComponent(visitId)}`;

/**
 * Hidden check: does a PDF exist on r.elhamylabs.com for this visit? Only a definite answer (404 or not a PDF) is "no";
 * a network failure counts as "yes" so a blip does not hide a real result. The body is cancelled, only headers are read.
 */
export const resultExistsOnLink = async (visitId: string): Promise<boolean> => {
  try {
    const response = await fetch(resultLinkFor(visitId));
    const isPdf = (response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf');
    void response.body?.cancel();
    return response.ok && isPdf;
  } catch {
    return true;
  }
};

/** Fetches the PDF straight from r.elhamylabs.com so the browser can save it. Null means "use the server download instead". */
export const fetchResultFromLink = async (resultUrl: string): Promise<Blob | null> => {
  if (!resultUrl.startsWith(RESULT_LINK_PREFIX)) return null;

  try {
    const response = await fetch(resultUrl);
    if (!response.ok) return null;
    if (!(response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf')) return null;
    return await response.blob();
  } catch {
    return null;
  }
};

export interface ResultRequestStatus {
  isPending: boolean;
  isLocked: boolean;
}

/** Why a result request was refused; "failed" covers everything unexpected. */
export type ResultRequestError = 'already-requested' | 'payment-required' | 'not-found' | 'failed';

interface WireResultRequestStatus {
  isPending?: boolean;
  isLocked?: boolean;
}

export const mapResultRequestStatus = (wire: WireResultRequestStatus): ResultRequestStatus => ({
  isPending: Boolean(wire.isPending),
  isLocked: Boolean(wire.isLocked),
});

export const resultRequestErrorFor = (status: number): ResultRequestError => {
  if (status === 409) return 'already-requested';
  if (status === 402) return 'payment-required';
  if (status === 404) return 'not-found';
  return 'failed';
};

export const fetchResultRequestStatus = async (token: string, visitId: string): Promise<ResultRequestStatus> =>
  mapResultRequestStatus(
    await portalJson<WireResultRequestStatus>(
      `/api/portal/me/visits/${encodeURIComponent(visitId)}/result-request`,
      { token },
    ),
  );

/** Source 1 = website. The server checks the visit belongs to the signed-in customer and has no unpaid balance. */
export const requestVisitResult = async (token: string, visitId: string): Promise<void> => {
  const response = await portalFetch(`/api/portal/me/visits/${encodeURIComponent(visitId)}/result-request`, {
    method: 'POST',
    token,
    body: { source: 1 },
  });
  if (!response.ok) throw new PortalApiError(response.status, resultRequestErrorFor(response.status));
};

export interface PortalComplaint {
  complaintId: number;
  body: string;
  createdDate: string;
  isHandled: boolean;
}

export const fetchComplaints = async (token: string): Promise<PortalComplaint[]> => {
  return portalJson<PortalComplaint[]>('/api/portal/me/complaints', { token });
};

export const submitComplaint = async (
  token: string,
  input: { email: string; phone: string; body: string },
): Promise<void> => {
  const response = await portalFetch('/api/portal/me/complaints', {
    method: 'POST',
    token,
    body: { email: input.email.trim(), phone: input.phone.trim(), body: input.body.trim() },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const changeCustomerPassword = async (
  token: string,
  input: { currentPassword: string; newPassword: string },
): Promise<void> => {
  const response = await portalFetch('/api/portal/me/password', {
    method: 'POST',
    token,
    body: { currentPassword: input.currentPassword, newPassword: input.newPassword },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const deleteCustomerAccount = async (token: string, password: string): Promise<void> => {
  const response = await portalFetch('/api/portal/me/account/delete', {
    method: 'POST',
    token,
    body: { password },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};
